// Hideo - what every online storage service shares: the app keys, the stored folder names, the state of each service on
// this device (src/stores/sync.ts, localStorage creatorOS_drive), errors and file extensions.
// Ported from the legacy cloud/cloud.js (verbatim copy in tests/fixtures/legacy/cloud.js, golden tests in tests/saving*.test.ts).
import { useSyncStore, type CloudState } from '../../stores/sync'
import { CONFIG } from '../../config'

/** Public keys of the apps registered by the user (empty = service not configured yet); from VITE_DROPBOX_APP_KEY */
export const CLOUD_KEYS = { dropbox: CONFIG.dropboxAppKey }
// Folder names on the services are stored data: never rename or translate them
export const CLOUD_FOLDER = 'Hideo'
export const MEDIA_FOLDERS: Record<'img' | 'voice', string> = { img: 'Immagini', voice: 'Vocali' }
/** The page is served from a website (online saving works), not opened as a file */
export const driveOnline = /^https?:$/.test(location.protocol)

/** Normalised file "meta" of every service. version = what changes at every save: it tells whether another device has
 *  saved in the meantime */
export interface CloudMeta {
  id: string
  name: string
  modified?: string
  savedAt?: string
  savedOn?: string
  version?: string
}
/** An access token still good for at least a minute */
export const tokenFresh = (exp: number | undefined): boolean => !!exp && exp > Date.now() + 60000
/** Runs a request; a 404 (the file or folder is not there) gives `missing` instead of an error */
export async function unless404<T, M>(run: () => Promise<T>, missing: M): Promise<T | M> {
  try { return await run() } catch (e) { if ((e as CloudError).status === 404) return missing; throw e }
}
/** Save date and device written with a profile file, and the profile it belongs to ('copia' for a safety copy) */
interface SaveInfo { savedAt: string; savedOn: string; profileId: string }
/** An online storage service. Each one only sees its own app folder: Google "Hideo" (drive.file), Dropbox "Applicazioni/Hideo" */
export interface CloudService {
  id: string
  name: string
  /** Font Awesome classes of its logo */
  icon: string
  /** Where the files go, in words */
  place: string
  /** Hideo can use it (its app key is set) */
  configured(): boolean
  /** The sign-in token; interactive = may open the service's window (only inside a click) */
  token(interactive: boolean): Promise<string | null>
  connect(): Promise<{ email: string }>
  disconnect(): void
  /** The profile files in the app's folder, newest first */
  listProfiles(): Promise<CloudMeta[]>
  meta(id: string): Promise<CloudMeta | null>
  findProfile(fileName: string, profileId: string): Promise<CloudMeta | null>
  saveProfile(id: string | null | undefined, fileName: string, text: string, info: SaveInfo): Promise<CloudMeta>
  readText(id: string): Promise<string>
  findMedia(mediaId: string): Promise<string>
  uploadMedia(kind: 'img' | 'voice', fileName: string, blob: Blob, mediaId: string): Promise<string>
  readBlob(id: string): Promise<{ buf: ArrayBuffer; type: string }>
}

/** Writes every service's state to this browser */
export const saveDriveState = (): void => { try { localStorage.setItem('creatorOS_drive', JSON.stringify(useSyncStore().DRIVE)) } catch { /* storage full or blocked */ } }
/** A service's state on this device, created when missing */
export const CS = (id: string): CloudState => useSyncStore().CS(id)
/** The service is connected on this device */
export const cloudOn = (id: string): boolean => useSyncStore().cloudOn(id)

/** An error of a service: message for the user, HTTP status, what the service answered */
export interface CloudError extends Error { status?: number; detail?: unknown }
export const cloudErr = (msg: string, status?: number, detail?: unknown): CloudError => { const e = new Error(msg) as CloudError; e.status = status; e.detail = detail; return e }

export const fileExt = (type: string, kind: string): string => ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/mpeg': 'mp3', 'audio/ogg': 'ogg', 'audio/wav': 'wav', 'audio/x-m4a': 'm4a', 'audio/aac': 'aac' } as Record<string, string>)[type] || (kind === 'voice' ? 'webm' : 'jpg')
